/**
 * @fileoverview Utility functions for calculating metrics from API data.
 * @module shared/utils/metricsUtils
 */

import { MetricCardData } from '@/shared/types/metrics'
import { formatMoney } from '@/shared/utils/money'

/**
 * Minimal shape of a time-series data point used by these helpers.
 */
interface TimeSeriesPoint {
  salesTotal: number
  totalExpenses: number
  adSpend: { total: number }
  costOfGoods: number
  profit: number
}

/**
 * Aggregated totals calculated from time-series data.
 */
export interface AggregatedTotals {
  /** Total revenue across all periods */
  totalRevenue: number
  /** Total costs across all periods */
  totalCosts: number
  /** Total advertising spend */
  totalAdSpend: number
  /** Total cost of goods sold */
  totalCogs: number
  /** Net profit (revenue - costs) */
  netProfit: number
  /** Profit margin percentage */
  margin: number
}

/**
 * Calculates aggregated totals from ecommerce time-series data.
 *
 * @param dataPoints - Array of ecommerce statistics from API
 * @returns Aggregated totals across all time periods
 * @example
 * ```typescript
 * const totals = calculateTotalsFromTimeSeries(statistics.dataPoints);
 * console.log(totals.totalRevenue); // 425800
 * ```
 */
export function calculateTotalsFromTimeSeries(dataPoints: TimeSeriesPoint[]): AggregatedTotals {
  if (!dataPoints || dataPoints.length === 0) {
    return {
      totalRevenue: 0,
      totalCosts: 0,
      totalAdSpend: 0,
      totalCogs: 0,
      netProfit: 0,
      margin: 0,
    }
  }

  const totals = dataPoints.reduce(
    (acc, dataPoint) => ({
      totalRevenue: acc.totalRevenue + dataPoint.salesTotal,
      totalCosts: acc.totalCosts + dataPoint.totalExpenses,
      totalAdSpend: acc.totalAdSpend + dataPoint.adSpend.total,
      totalCogs: acc.totalCogs + dataPoint.costOfGoods,
      netProfit: acc.netProfit + dataPoint.profit,
    }),
    {
      totalRevenue: 0,
      totalCosts: 0,
      totalAdSpend: 0,
      totalCogs: 0,
      netProfit: 0,
    }
  )

  // Calculate profit margin as percentage
  const margin = totals.totalRevenue > 0 ? (totals.netProfit / totals.totalRevenue) * 100 : 0

  return {
    ...totals,
    margin,
  }
}

/**
 * Formats a number as currency with full amount and 1 decimal place using Polish formatting.
 *
 * @param amount - Numeric amount to format
 * @param currency - Currency symbol (default: 'zł' for Polish złoty)
 * @returns Formatted currency string with Polish number format
 * @example
 * ```typescript
 * formatCurrency(425800); // '425 800,0 zł'
 * formatCurrency(1250000); // '1 250 000,0 zł'
 * ```
 */
export function formatCurrency(amount: number): string {
  // Polish locale formatting for legacy call sites; prefer Money/formatMoney elsewhere
  return formatMoney(amount, 'PLN', 'pl-PL', 1, 1, 'narrowSymbol')
}

/**
 * Formats a number as currency without decimal places and currency symbol.
 *
 * @param amount - The number to format
 * @returns Formatted number string with thousand separators (e.g., "107 043")
 */
/**
 * Formats a number as currency without decimal places and smaller currency symbol.
 *
 * @param amount - The number to format
 * @returns Formatted number string with thousand separators and smaller currency symbol (e.g., "107 043 zł")
 */
export function formatCurrencyNoDecimals(amount: number): string {
  return formatMoney(amount, 'PLN', 'pl-PL', 0, 0, 'narrowSymbol')
}

/**
 * Formats a number as currency without thousand separators or decimals.
 *
 * @param amount - Numeric amount to format
 * @param currency - Currency symbol (default: 'zł')
 * @returns Formatted currency string without separators (e.g., "141496 zł")
 * @example
 * ```typescript
 * formatCurrencyCompact(141496.8); // '141496 zł'
 * ```
 */
export function formatCurrencyCompact(amount: number): string {
  // Derive PLN symbol via Intl to avoid hardcoding
  const symbolSample = formatMoney(0, 'PLN', 'pl-PL', 0, 0, 'narrowSymbol') // e.g., '0 zł'
  const symbol = symbolSample.replace(/[\s\d.,-]/g, '')
  // Use compact K-format only for hundreds of thousands (100,000–999,999)
  if (amount >= 100_000 && amount < 1_000_000) {
    const thousands = Math.floor(amount / 1_000)
    return `${thousands}K ${symbol}`
  }
  // For other ranges, return integer without separators/decimals
  return `${Math.trunc(amount)} ${symbol}`
}

/**
 * React component that formats currency with smaller currency symbol.
 *
 * @param amount - The number to format
 * @returns JSX element with formatted number and smaller currency symbol
 */
/**
 * Formats a number as currency without decimal places for inline display.
 *
 * @param amount - The number to format
 * @returns Formatted number string with thousand separators and currency symbol (e.g., "107 043 zł")
 */
export function formatCurrencyInline(amount: number): string {
  return formatMoney(amount, 'PLN', 'pl-PL', 0, 0, 'narrowSymbol')
}

/**
 * Formats a percentage with proper decimal places.
 *
 * @param percentage - Percentage as decimal (e.g., 0.155 for 15.5%)
 * @param decimalPlaces - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 * @example
 * ```typescript
 * formatPercentage(0.155); // '15.5%'
 * formatPercentage(0.3548, 2); // '35.48%'
 * ```
 */
export function formatPercentage(percentage: number, decimalPlaces = 1): string {
  return `${(percentage * 100).toFixed(decimalPlaces)}%`
}

/**
 * Formats a percentage with proper decimal places (alias for clarity).
 *
 * @param percentage - Percentage as ratio (e.g., 0.155 for 15.5%)
 * @param digits - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 * @example
 * ```typescript
 * formatPercent(0.155); // '15.5%'
 * formatPercent(0.3548, 2); // '35.48%'
 * ```
 */
export function formatPercent(percentage: number, digits = 1): string {
  return formatPercentage(percentage, digits)
}

/**
 * Formats a number as an integer with locale-specific formatting.
 *
 * @param n - Number to format
 * @returns Formatted integer string with Polish locale
 * @example
 * ```typescript
 * formatInt(1234567); // '1 234 567'
 * formatInt(42); // '42'
 * ```
 */
export function formatInt(n: number): string {
  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 0,
  }).format(Math.round(n))
}

/**
 * Calculates trend percentage comparing two periods.
 *
 * @param currentValue - Current period value
 * @param previousValue - Previous period value
 * @returns Trend object with percentage and direction
 */
function calculateTrend(
  currentValue: number,
  previousValue: number
): { value: number; direction: 'up' | 'down' } {
  if (previousValue === 0) {
    return { value: 0, direction: 'up' }
  }

  const percentChange = ((currentValue - previousValue) / previousValue) * 100
  return {
    value: Math.abs(percentChange),
    direction: percentChange >= 0 ? 'up' : 'down',
  }
}

/**
 * Converts aggregated totals to MetricCard data structures with real trend calculations.
 *
 * @param totals - Aggregated totals from time-series data
 * @param dataPoints - Original time-series data for trend calculation
 * @returns Object containing revenue and cost card data
 * @example
 * ```typescript
 * const totals = calculateTotalsFromTimeSeries(dataPoints);
 * const { revenueCard, costCard } = convertTotalsToMetricCards(totals, dataPoints);
 * ```
 */
export function convertTotalsToMetricCards(
  totals: AggregatedTotals,
  dataPoints: TimeSeriesPoint[]
): {
  revenueCard: MetricCardData
  costCard: MetricCardData
} {
  // Calculate trends by comparing recent periods
  let revenueTrend: { value: number; direction: 'up' | 'down' } = { value: 0, direction: 'up' }
  let costTrend: { value: number; direction: 'up' | 'down' } = { value: 0, direction: 'up' }

  if (dataPoints && dataPoints.length >= 2) {
    // Compare last two periods for trend
    const lastPeriod = dataPoints[dataPoints.length - 1]
    const secondLastPeriod = dataPoints[dataPoints.length - 2]

    if (lastPeriod && secondLastPeriod) {
      const recentRevenue = lastPeriod.salesTotal
      const olderRevenue = secondLastPeriod.salesTotal
      const recentCosts = lastPeriod.totalExpenses
      const olderCosts = secondLastPeriod.totalExpenses

      revenueTrend = calculateTrend(recentRevenue, olderRevenue)
      costTrend = calculateTrend(recentCosts, olderCosts)
    }
  }

  const revenueCard: MetricCardData = {
    title: 'TOTAL REVENUE',
    value: formatCurrency(totals.totalRevenue),
    trend: {
      value: Number(revenueTrend.value.toFixed(1)),
      direction: revenueTrend.direction,
      period: 'vs prev period',
    },
    secondaryMetrics: [
      {
        label: 'Net Profit',
        value: formatCurrency(totals.netProfit),
      },
      {
        label: 'Margin',
        value: formatPercentage(totals.margin / 100),
        trend: 'up',
      },
    ],
  }

  const costCard: MetricCardData = {
    title: 'TOTAL COST',
    value: formatCurrency(totals.totalCosts),
    trend: {
      value: Number(costTrend.value.toFixed(1)),
      direction: costTrend.direction,
      period: 'vs prev period',
    },
    secondaryMetrics: [
      {
        label: 'Ad Spend',
        value: formatCurrency(totals.totalAdSpend),
      },
      {
        label: 'COGS',
        value: formatCurrency(totals.totalCogs),
      },
    ],
  }

  return { revenueCard, costCard }
}
